if (!customElements.get('advanced-video')) {
  class AdvancedVideo extends HTMLElement {
    constructor () {
      super();
      this.video = this.getAttribute('data-video');
      this.poster = this.getAttribute('data-poster');
      this.instance = null;
      this.autoplay = this.hasAttribute('data-autoplay');
      this.primaryControl = null;
      this.mutedControls = [];
      this.soundToggleControls = [];
      this.controlsStyle = this.getAttribute('data-controls');
      this.timeOutID = null;
      this.fallbackTriggered = false;
      this.isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
      this.firstPause = false;
      this.abortController = null;
      this.videoReadyInterval = null;
      this.fallbackVideo = null;
    }

    cacheDomReferences () {
      this.video = this.getAttribute('data-video');
      this.poster = this.getAttribute('data-poster');
      this.autoplay = this.hasAttribute('data-autoplay');
      this.primaryControl = this.querySelector('.advanced-video-primary-controls');
      this.mutedControls = Array.from(this.querySelectorAll('.advanced-video-muted, [data-video-sound-toggle]'));
      this.soundToggleControls = Array.from(this.querySelectorAll('[data-video-sound-toggle]'));
      this.controlsStyle = this.getAttribute('data-controls');
    }

    getCurrentVideo () {
      return this.fallbackVideo || this.instance?.getVideo?.() || null;
    }

    triggerFallbackVideo () {
      if (this.fallbackTriggered) return;

      this.fallbackTriggered = true;
      if (this.instance && this.instance.getVideo) {
        const previousVideo = this.instance.getVideo();
        if (previousVideo && !previousVideo.paused) {
          try {
            previousVideo.pause();
            previousVideo.currentTime = 0;
          } catch (e) {
            console.warn('Failed to stop previous video before fallback', e);
          }
        }
      }
      const container = this.querySelector('.video-container');
      if (!container) return;

      container.style.position = 'relative';
      container.style.overflow = 'hidden';
      container.style.backgroundImage = `url(${this.poster})`;
      container.style.backgroundSize = 'cover';
      container.style.backgroundPosition = 'center';
      container.style.backgroundRepeat = 'no-repeat';

      container.innerHTML = '';
      this.primaryControl?.remove();
      this.mutedControls
        .filter((control) => !control.matches('[data-video-sound-toggle]'))
        .forEach((control) => control.remove());

      const fallbackVideo = document.createElement('video');
      const shouldShowControls = this.controlsStyle !== 'false';
      fallbackVideo.src = this.video;
      fallbackVideo.poster = this.poster;
      fallbackVideo.setAttribute('playsinline', '');
      fallbackVideo.controls = shouldShowControls;
      fallbackVideo.autoplay = this.autoplay;
      fallbackVideo.loop = this.autoplay;
      fallbackVideo.muted = this.autoplay;
      fallbackVideo.defaultMuted = this.autoplay;
      if (this.autoplay) {
        fallbackVideo.volume = 0;
        this.setAttribute('data-muted', '1');
      }

      this.fallbackVideo = fallbackVideo;
      this.updateMutedControls(fallbackVideo);

      Object.assign(fallbackVideo.style, {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
        position: 'absolute',
        top: '0',
        left: '0',
        zIndex: '1',
        opacity: '0',
        transition: 'opacity 0.5s ease',
      });

      container.appendChild(fallbackVideo);
      this.setVideoReady(fallbackVideo);

      fallbackVideo.play().then(() => {
        this.setVideoReady(fallbackVideo, true);

        requestAnimationFrame(() => {
          fallbackVideo.style.opacity = '1';
        });

        setTimeout(() => {
          container.style.backgroundImage = 'none';
        }, 600);
      }).catch(err => {
        console.warn('Fallback video play failed', err);
        this.setVideoReady(fallbackVideo, true);
        fallbackVideo.style.opacity = '1';
        container.style.backgroundImage = 'none';
      });

      this.setAttribute('data-controls', 'fallback');
    }

    stoppedVideo (operation = 'ended') {
      if (this.fallbackTriggered) return;

      const interval = operation === 'paused' && this.timeOutID === null ? 3000 : 0;
      this.timeOutID = setTimeout(() => {
        const video = this.instance.getVideo();
        let stopped = true;

        if (operation === 'ended') {
          video.currentTime = 0;
        } else if (operation === 'paused' && !video.paused) {
          stopped = false;
        }

        if (stopped) {
          video.controls = false;
          this.primaryControl?.style.removeProperty('display');
          this.setAttribute('data-playing', '0');
        }

        this.timeOutID = null;
      }, interval);
    }

    stoppedVideoHandle () {
      this.stoppedVideo();
    }

    playingVideoHandle () {
      if (this.fallbackTriggered) return;

      const video = this.getCurrentVideo();
      video.controls = true;
      this.primaryControl?.style.setProperty('display', 'none');
      this.setAttribute('data-playing', '1');
    }

    pausedVideoHandle () {
      if (this.firstPause) {
        this.stoppedVideo('ended');
      } else if (!this.isTouchDevice) {
        this.stoppedVideo('paused');
      }
    }

    primaryControlHandle (e) {
      e.preventDefault();
      e.stopPropagation();

      const video = this.getCurrentVideo();
      if (!video) return;

      if (!video.paused) {
        video.pause();
        this.setAttribute('data-playing', '0');
        return;
      }

      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise.then(() => {

          this.setAttribute('data-playing', '1');
          if (this.controlsStyle === 'default') {
            video.controls = true;
            this.primaryControl.style.display = 'none';
          }
          const startTime = video.currentTime;
          setTimeout(() => {
            const nowTime = video.currentTime;
            if (Math.abs(nowTime - startTime) < 0.05) {
              console.warn('[video] Video playing, but not visible — triggering fallback');
              this.triggerFallbackVideo();
            }
          }, 1500);
        }).catch((err) => {
          console.warn('[video] Play failed:', err);
          this.triggerFallbackVideo();
        });
      }
    }

    mutedControlHandle (e) {
      e.preventDefault();
      e.stopPropagation();

      const video = this.getCurrentVideo();
      if (video) {
        const shouldUnmute = video.muted || video.volume === 0;

        video.muted = !shouldUnmute;
        video.volume = shouldUnmute ? 1 : 0;

        if (video.paused) {
          video.play().catch(err => console.log('Play after mute toggle failed', err));
        }

        this.updateMutedControls(video);
      }
    }

    updateMutedControls (video = null) {
      const isMuted = !video || video.muted || video.volume === 0;

      this.setAttribute('data-muted', isMuted ? '1' : '0');

      this.soundToggleControls.forEach((control) => {
        const mutedText = control.dataset.mutedText || control.getAttribute('aria-label') || '';
        const unmutedText = control.dataset.unmutedText || mutedText;
        const label = control.querySelector('[data-video-sound-toggle-label]');
        const text = isMuted ? mutedText : unmutedText;

        control.setAttribute('aria-label', text);
        control.setAttribute('aria-pressed', isMuted ? 'false' : 'true');

        if (label) {
          label.textContent = text;
        }
      });
    }

    setVideoReady (video, force = false) {
      if (!video || this.getAttribute('data-video-ready') === '1') return;

      const markReady = () => {
        this.setAttribute('data-video-ready', '1');
      };

      if (force || video.readyState >= 2 || !video.paused || video.currentTime > 0) {
        markReady();
        return;
      }

      video.addEventListener('loadeddata', markReady, { once: true, signal: this.abortController.signal });
      video.addEventListener('canplay', markReady, { once: true, signal: this.abortController.signal });
      video.addEventListener('playing', markReady, { once: true, signal: this.abortController.signal });
      video.addEventListener('timeupdate', markReady, { once: true, signal: this.abortController.signal });
    }

    connectedCallback () {
      this.abortController = new AbortController();
      this.cacheDomReferences();
      this.updateMutedControls();

      const params = {
        loop: this.autoplay,
        muted: true,
        controls: false,
        autoplay: this.autoplay,
        playsInline: true,
      };

      if (this.video && this.id) {
        this.instance = new vidbg(`#${this.id} .video-container`, {
          mp4: this.video,
          poster: this.poster,
        }, { ...params });

        this.videoReadyInterval = setInterval(() => {
          const video = this.instance.getVideo();
          if (video) {
            this.setVideoReady(video);
            clearInterval(this.videoReadyInterval);
            this.videoReadyInterval = null;
          }
        }, 50);

        if (!this.autoplay) {
          const waitForVideo = setInterval(() => {
            const video = this.instance.getVideo();
            if (video) {
              this.setVideoReady(video);

              video.addEventListener('canplay', () => {
                this.firstPause = true;
                video.pause();
                setTimeout(() => {
                  this.firstPause = false;
                }, 0);
                video.muted = false;
                video.volume = 1;
                this.updateMutedControls(video);

                if (this.controlsStyle === 'default') {

                  video.addEventListener('pause', this.pausedVideoHandle.bind(this));
                  video.addEventListener('ended', this.stoppedVideoHandle.bind(this));
                  video.addEventListener('playing', this.playingVideoHandle.bind(this));
                }
              }, { once: true });

              clearInterval(waitForVideo);
            }
          }, 50);
        } else {
          const autoplayCheck = setTimeout(() => {
            const video = this.instance.getVideo();
            if (!video) return;

            const failedToPlay = video.paused || video.readyState < 2 || video.currentTime < 0.05;

            if (failedToPlay) {
              console.warn('[video] Autoplay failed — triggering fallback');
              this.triggerFallbackVideo();
            } else {
              this.setVideoReady(video, true);
            }
          }, 1500);
        }

        if (this.primaryControl) {
          this.primaryControl.addEventListener('click', this.primaryControlHandle.bind(this), {
            signal: this.abortController.signal,
          });
        }

        if (this.controlsStyle !== 'default' && this.mutedControls.length) {
          this.mutedControls.forEach((control) => {
            control.addEventListener('click', this.mutedControlHandle.bind(this), {
              signal: this.abortController.signal,
            });
          });
        }
      }
    }

    disconnectedCallback () {
      this.abortController?.abort();

      if (this.videoReadyInterval) {
        clearInterval(this.videoReadyInterval);
        this.videoReadyInterval = null;
      }

      if (this.instance) {
        const video = this.instance.getVideo?.();
        if (video && !video.paused) {
          video.pause();
        }
        this.instance.destroy();
      }

      if (this.fallbackVideo && !this.fallbackVideo.paused) {
        this.fallbackVideo.pause();
      }

      const container = this.querySelector('.video-container');
      if (container) {
        container.innerHTML = '';
      }
    }
  }

  customElements.define('advanced-video', AdvancedVideo);
}
